import { MLS, MLSID, isMLSID, isUndefined } from '@aloft/toolkit-shared';
import { Block, Button, Flex, SimpleSingleCombo, Typography } from '@aloft/toolkit-sui';

import { useMemo, useState } from 'react';

import { AccountModuleDeps } from '../types.js';
import { MLSList } from './MLSList.js';
import { MLSListItem } from './MLSListItem.js';

import './MLSContent.scss';

type Props = {
  account: AccountModuleDeps['account'];
  updateAccount: AccountModuleDeps['updateAccount'];
};

export function MLSContent({ account, updateAccount }: Props) {
  const [showAddMLSForm, setShowMLSForm] = useState(false);

  const unsortedMLSIDs = account.mlsIDs;

  const sortedMLSIDsForDisplayOnly = [...unsortedMLSIDs].sort();

  const [selectedMLS, setSelectedMLS] = useState<MLSID>();
  const unselectedMLSOptions = useMemo(() => {
    const selectedMLSIDs = new Set(unsortedMLSIDs);
    return MLS._keys
      .filter((mlsID) => !selectedMLSIDs.has(mlsID))
      .map((v) => {
        const { longName } = MLS[v];
        return {
          label: longName,
          value: v,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [unsortedMLSIDs]);

  const saveMLS = () => {
    if (isUndefined(selectedMLS)) return;
    updateAccount({
      ...account,
      mlsIDs: [...unsortedMLSIDs, selectedMLS],
    });
    setSelectedMLS(undefined);
  };

  const removeMLS = (mlsID: MLSID) => {
    updateAccount({
      ...account,
      mlsIDs: unsortedMLSIDs.filter((id) => id !== mlsID),
    });
  };

  return (
    <>
      <Typography variant="h4">My MLSes</Typography>
      <Block className="mls-list-container">
        <MLSList>
          {sortedMLSIDsForDisplayOnly.map((mlsID) => (
            <MLSListItem key={mlsID} mlsID={mlsID} onRemove={removeMLS} />
          ))}
        </MLSList>
        <Block>
          {showAddMLSForm ? (
            <Flex flexDirection="column" marginTop="8px" gap="8px">
              <SimpleSingleCombo
                options={unselectedMLSOptions}
                value={selectedMLS}
                onChange={(value) => {
                  if (isMLSID(value)) {
                    setSelectedMLS(value);
                  } else {
                    setSelectedMLS(undefined);
                  }
                }}
                size="sm"
                placeholder="Select MLS"
              />
              <Flex marginLeft="auto" gap="4px">
                <Button
                  size="sm"
                  variant="secondary"
                  text="Cancel"
                  onClick={() => {
                    setShowMLSForm(false);
                    setSelectedMLS(undefined);
                  }}
                />
                <Button disabled={!selectedMLS} size="sm" text="Add" onClick={saveMLS} />
              </Flex>
            </Flex>
          ) : (
            <Button
              variant="inline"
              text="Add MLS (Optional)"
              icon="add-circle"
              onClick={() => setShowMLSForm(true)}
            />
          )}
        </Block>
      </Block>
    </>
  );
}
